/**
 * Nursery paint colour coding: paints, the standing scheme, and the bands
 * painted on the trees (docs/nursery-colour-coding-plan.md).
 */

export type ColourBy = 'cultivar' | 'combination';
export type PaintRole = 'single' | 'primary' | 'secondary' | 'manual';
export type SubjectType = 'cultivar' | 'rootstock' | 'combination';
export type Band = 'bottom' | 'top' | 'root';

export const BAND_NAME: Record<Band, string> = { bottom: 'Bottom', top: 'Top', root: 'Root' };

export interface Paint {
  id: number;
  paint_number: number;
  name: string;
  hex: string;
  role: PaintRole;
  active: boolean;
  sort_order: number;
}

export interface ColourSettings {
  enabled: boolean;
  colour_by: ColourBy;
  no_colour_below: number;
  single_only_above: number;
  updated_at: string | null;
}

export type SchemeAction = 'keep' | 'assign' | 'change' | 'none';

export interface SchemeRow {
  key: string;
  subject_type: SubjectType;
  cultivar_id: number | null;
  cultivar_name: string | null;
  rootstock_id: number | null;
  rootstock_name: string | null;
  /** Trees standing in active lots. */
  count: number;
  /** The botanical species group: lines in one group never share a colour. */
  group: string;
  group_source: string;
  /** A live lot already carries this code's paint. */
  painted: boolean;
  code: { id?: number; paint_1_id: number; paint_2_id: number | null; source: 'auto' | 'manual'; updated_at?: string } | null;
  proposed: { action: SchemeAction; paint_1_id: number | null; paint_2_id: number | null };
}

export type SchemeProblemKind = 'no_free_colour' | 'retired_paint' | 'large_with_pair' | 'duplicate_in_group' | 'missing_species';

export interface SchemeProblem {
  key: string;
  kind: SchemeProblemKind;
  with?: string[];
}

export interface SchemeResponse {
  settings: ColourSettings;
  mode: ColourBy;
  paints: Paint[];
  applied: boolean;
  written: number;
  rows: SchemeRow[];
  problems: SchemeProblem[];
}

export interface SetCodeResult {
  key: string;
  paint_1_id: number;
  paint_2_id: number | null;
  clashes: { key: string; name: string }[];
  painted_lots_with_previous: number;
}

export interface ColourHistoryRow {
  id: number;
  subject_type: SubjectType;
  cultivar_id: number | null;
  cultivar_name: string | null;
  rootstock_id: number | null;
  rootstock_name: string | null;
  old_label: string | null;
  new_label: string | null;
  source: 'auto' | 'manual';
  reason: string | null;
  changed_at: string;
  changed_by_name: string | null;
}

// ── Painting ──

export interface PaintSnapshot {
  id: number;
  paint_number: number;
  name: string;
  hex: string;
}

/** A painting as recorded: the paint as it went on, kept even if the scheme moves. */
export interface LotPainting {
  id: number;
  lot_id: number;
  band: Band;
  code_id: number | null;
  paint_id: number | null;
  paint_number: number | null;
  paint_name: string;
  paint_hex: string;
  quantity: number | null;
  painted_at: string;
  painted_by_name?: string | null;
  copied_from_id?: number | null;
}

/**
 * - to_paint: the scheme asks for it, nothing on yet
 * - painted: on, in the scheme's colour
 * - repaint: on, but in a colour the scheme has since changed
 * - not_in_scheme: on, but the scheme no longer asks for this band
 */
export type BandState = 'to_paint' | 'painted' | 'repaint' | 'not_in_scheme';

export interface LotBand {
  band: Band;
  paint: PaintSnapshot | null;
  painting: LotPainting | null;
  state: BandState;
}

export interface PaintWalkLot {
  lot_id: number;
  lot_code: string | null;
  current_count: number;
  current_stage: string;
  cultivar_id: number;
  rootstock_id: number | null;
  cultivar_name: string;
  rootstock_name: string | null;
  tree_type_name: string;
  location_id: number | null;
  location_name: string | null;
  row_number: number | null;
  row_side: 'left' | 'right' | null;
  sequence_number: number | null;
  block_id: number | null;
  block_name: string | null;
  block_sort_order: number | null;
  farm_name: string;
  bands: LotBand[];
}

export interface PaintWalk {
  enabled: boolean;
  colour_by: ColourBy;
  farm_name: string;
  block_name: string | null;
  lots: PaintWalkLot[];
}

export interface BandsResponse {
  enabled: boolean;
  colour_by: ColourBy;
  lots: Record<string, LotBand[]>;
}

export interface PaintingPayload {
  lot_id: number;
  band: Band;
  client_uuid: string;
  painted_at: string;
}
